import React, { useState, useEffect, useCallback } from "react";
import { X, CheckCircle } from "lucide-react";

const SOCIAL_PROOF = [
  { name: "Stefano — Amor Que Cura", pkg: "START", method: "amorquecura.com", tag: "#grazieStefano", real: true },
];

const PKG_COLORS = {
  START: "#EA4335",
  PLUS: "#FBBC04",
  ROOFTOP: "#34A853",
};

// Helper: read spots from localStorage
export const getSpots = () => {
  const raw = localStorage.getItem("sbp-spots-remaining");
  return raw ? Math.max(parseInt(raw, 10), 2) : 10;
};

// Subtle "cha-ching" sound via Web Audio API
const playChaChingSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);

    // First "cha" — bright metallic tap
    const o1 = ctx.createOscillator();
    o1.type = "sine";
    o1.frequency.setValueAtTime(2400, ctx.currentTime);
    o1.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
    o1.connect(gain);
    o1.start(ctx.currentTime);
    o1.stop(ctx.currentTime + 0.1);

    // Second "ching" — higher, ringing
    const o2 = ctx.createOscillator();
    o2.type = "sine";
    o2.frequency.setValueAtTime(3200, ctx.currentTime + 0.12);
    o2.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.5);
    const g2 = ctx.createGain();
    g2.connect(ctx.destination);
    g2.gain.setValueAtTime(0.06, ctx.currentTime + 0.12);
    g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    o2.connect(g2);
    o2.start(ctx.currentTime + 0.12);
    o2.stop(ctx.currentTime + 0.6);

    // Cleanup
    setTimeout(() => ctx.close(), 1000);
  } catch { /* audio not supported, silent fail */ }
};

const SocialProofToast = () => {
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);
  const [proofIndex, setProofIndex] = useState(0);
  const [hasDecrementedThisSession, setHasDecrementedThisSession] = useState(false);

  const showToast = useCallback((proof, shouldDecrement) => {
    setCurrent(proof);
    setVisible(true);
    playChaChingSound();

    if (shouldDecrement) {
      const spots = getSpots();
      const newSpots = Math.max(spots - 1, 2);
      localStorage.setItem("sbp-spots-remaining", String(newSpots));
      // Notify other components
      window.dispatchEvent(new CustomEvent("sbp-spots-update", { detail: newSpots }));
    }

    // Auto-hide after 8 seconds
    setTimeout(() => setVisible(false), 8000);
  }, []);

  useEffect(() => {
    // Check if we already decremented this session
    const sessionFlag = sessionStorage.getItem("sbp-session-decremented");
    if (sessionFlag) {
      setHasDecrementedThisSession(true);
    }

    // First toast after 60 seconds — this one decrements
    const firstTimer = setTimeout(() => {
      if (!sessionStorage.getItem("sbp-session-decremented")) {
        showToast(SOCIAL_PROOF[0], true);
        sessionStorage.setItem("sbp-session-decremented", "true");
        setHasDecrementedThisSession(true);
        setProofIndex(1);
      }
    }, 60000);

    return () => clearTimeout(firstTimer);
  }, [showToast]);

  // Subsequent toasts every ~2.5 minutes (just social proof, no decrement)
  useEffect(() => {
    // Stefano is the only entry — show only once, no repeat
    return;
  }, [hasDecrementedThisSession, showToast]);

  if (!visible || !current) return null;

  const color = PKG_COLORS[current.pkg] || "#FBBC04";

  return (
    <div
      className="fixed bottom-20 left-4 sm:left-6 z-[60] max-w-[320px] sm:max-w-[360px]"
      style={{ animation: "slideInLeft 0.5s cubic-bezier(0.22,1,0.36,1)" }}
      data-testid="social-proof-toast"
    >
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes countPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
      `}</style>
      <div className="bg-[#0c1220] border border-white/15 rounded-2xl shadow-2xl overflow-hidden" style={{ boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${color}20` }}>
        {/* Top bar with color */}
        <div className="h-1" style={{ background: color }}></div>
        <div className="px-4 py-3">
          {/* Close button */}
          <button
            onClick={() => setVisible(false)}
            className="absolute top-2 right-2 text-gray-500 hover:text-white p-1"
            data-testid="social-proof-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          {/* -1 badge */}
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-black text-white text-lg"
              style={{ background: color, animation: "countPulse 0.6s ease-out" }}
            >
              -1
            </div>
            <div className="flex-1 min-w-0 pr-4">
              <p className="text-white text-sm leading-snug">
                <strong>{current.name}</strong>{" "}
                {current.real ? (
                  <>ha acquistato il pacchetto <strong style={{ color }}>{current.pkg}</strong>!</>
                ) : (
                  <>ha pre-acquistato con <span className="text-gray-300">{current.method}</span> il pacchetto <strong style={{ color }}>{current.pkg}</strong>.</>
                )}
              </p>
              {current.real && current.method && (
                <a href={`https://${current.method}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-400 hover:text-white underline underline-offset-2 mt-0.5 inline-block">{current.method}</a>
              )}
              <p className="text-xs mt-1.5" style={{ color }}>
                {current.tag}
              </p>
            </div>
          </div>

          {/* Remaining spots */}
          <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-white/10">
            <CheckCircle className="w-3.5 h-3.5 text-[#34A853]" />
            <span className="text-gray-400 text-[11px]">
              Ancora <strong className="text-white">{getSpots()}</strong>/10 posti disponibili
            </span>
            <span className="text-[10px] text-gray-600 ml-auto">adesso</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialProofToast;
